/**
 * `/settings/agents` — Agentes conectados (OT-0043, Docs/15 §3.4).
 *
 * Lista los agentes de IA (Claude Code, Claude.ai, ChatGPT…) que el usuario
 * autorizó a consultar la documentación oficial con su identidad, y permite
 * desconectarlos. Un admin del tenant puede ver y desconectar los de toda la
 * organización. Arriba, la URL del servidor MCP (del backend, una sola fuente)
 * y el comando listo para Claude Code.
 */

import { useState, type ReactNode } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Check, Copy } from 'lucide-react'

import {
  AUTHOR_SCOPE,
  getAgentServer,
  listAgentConnections,
  revokeAgentConnection,
  type AgentConnection,
  type AgentConnectionScope,
} from '../../api/agentConnections'
import { LoginScreen } from '../../auth/LoginScreen'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { toIntlLocale } from '../../i18n/config'
import { canRevokeAgentAccess, isAdmin as isAdminTier } from '../../lib/tiers'
import { pushToast } from '../../stores/toastStore'

export function ConnectedAgentsView() {
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <ConnectedAgents />
      </AuthenticatedTemplate>
    </>
  )
}

function ConnectedAgents() {
  const { t, i18n } = useTranslation(['agents', 'common'])
  const errorMessage = useApiErrorMessage()
  const { accessToken } = useAccessToken()
  const { me } = useMe()
  const queryClient = useQueryClient()
  const isAdmin = isAdminTier(me?.role)
  // Revocar sólo quita acceso: se ofrece también en solo lectura (OT-0073).
  const canRevoke = canRevokeAgentAccess(me?.role)
  const [scope, setScope] = useState<AgentConnectionScope>('mine')
  const [pending, setPending] = useState<AgentConnection | null>(null)

  const effectiveScope: AgentConnectionScope = isAdmin ? scope : 'mine'

  const server = useQuery({
    queryKey: ['agent-server'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getAgentServer(accessToken)
    },
    enabled: !!accessToken,
    staleTime: Infinity,
  })

  const connections = useQuery({
    queryKey: ['agent-connections', effectiveScope],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listAgentConnections(accessToken, effectiveScope)
    },
    enabled: !!accessToken,
    retry: false,
  })

  const revoke = useMutation({
    mutationFn: async (connection: AgentConnection) => {
      if (!accessToken) throw new Error('no_access_token')
      await revokeAgentConnection(accessToken, connection.id)
      return connection
    },
    onSuccess: (connection) => {
      setPending(null)
      void queryClient.invalidateQueries({ queryKey: ['agent-connections'] })
      pushToast({
        kind: 'success',
        message: t('agents:connected.revoked', { client: connection.client_name }),
      })
    },
    onError: (err) => {
      setPending(null)
      pushToast({ kind: 'critical', message: errorMessage(err) })
    },
  })

  const dateFormat = new Intl.DateTimeFormat(toIntlLocale(i18n.language), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
  const formatDate = (iso: string | null) =>
    iso ? dateFormat.format(new Date(iso)) : t('agents:connected.never_used')

  return (
    <ScrollPage>
      <div className="max-w-5xl">
        <header className="mb-6">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
            {t('agents:connected.eyebrow')}
          </p>
          <h1 className="font-display text-2xl font-semibold text-ink">
            {t('agents:connected.title')}
          </h1>
          <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
            {t('agents:connected.subtitle')}
          </p>
        </header>

        {server.data && <HowToConnect mcpUrl={server.data.mcp_url} />}

        {isAdmin && (
          <div
            role="radiogroup"
            aria-label={t('agents:connected.title')}
            className="mt-8 inline-flex rounded-md border border-line bg-surface p-0.5"
          >
            {(['mine', 'tenant'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={scope === value}
                onClick={() => setScope(value)}
                className={`rounded px-3 py-1.5 font-ui text-xs focus:outline-none focus:ring-2 focus:ring-brand/40 ${
                  scope === value ? 'bg-brand-soft text-brand' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {t(value === 'mine' ? 'agents:connected.scope_mine' : 'agents:connected.scope_tenant')}
              </button>
            ))}
          </div>
        )}

        <section className="mt-4">
          {connections.isPending ? (
            <p className="font-body text-sm text-ink-subtle">{t('agents:connected.loading')}</p>
          ) : connections.isError ? (
            <p role="alert" className="rounded-md border border-critical/40 bg-critical-soft p-4 text-sm text-critical">
              {errorMessage(connections.error)}
            </p>
          ) : connections.data.length === 0 ? (
            <p className="rounded-md border border-dashed border-line p-6 text-center font-body text-sm text-ink-muted">
              {t('agents:connected.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-subtle">
                  <tr>
                    <Th>{t('agents:connected.col_agent')}</Th>
                    {effectiveScope === 'tenant' && <Th>{t('agents:connected.col_owner')}</Th>}
                    <Th>{t('agents:connected.col_access')}</Th>
                    <Th>{t('agents:connected.col_connected')}</Th>
                    <Th>{t('agents:connected.col_last_used')}</Th>
                    {canRevoke && <Th />}
                  </tr>
                </thead>
                <tbody>
                  {connections.data.map((connection) => (
                    <tr key={connection.id} className="border-t border-line">
                      <Td>
                        <p className="font-ui text-sm text-ink">{connection.client_name}</p>
                        <p className="font-mono text-xs text-ink-subtle">{connection.client_domain}</p>
                      </Td>
                      {effectiveScope === 'tenant' && <Td>{connection.owner_name}</Td>}
                      <Td>
                        {connection.scopes.includes(AUTHOR_SCOPE)
                          ? t('agents:connected.access_author')
                          : t('agents:connected.access_read')}
                      </Td>
                      <Td>{formatDate(connection.created_at)}</Td>
                      <Td>{formatDate(connection.last_used_at)}</Td>
                      {canRevoke && (
                        <Td className="text-right">
                          <button
                            type="button"
                            onClick={() => setPending(connection)}
                            disabled={revoke.isPending}
                            className="rounded-md border border-line bg-surface px-3 py-1.5 text-xs text-ink-muted
                                       hover:text-critical hover:border-critical/40 disabled:opacity-50
                                       focus:outline-none focus:ring-2 focus:ring-brand/40"
                          >
                            {t('agents:connected.revoke')}
                          </button>
                        </Td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={pending !== null}
        tone="danger"
        title={t('agents:connected.revoke_title', { client: pending?.client_name ?? '' })}
        body={t('agents:connected.revoke_body', { client: pending?.client_name ?? '' })}
        confirmLabel={t('agents:connected.revoke_confirm')}
        cancelLabel={t('common:actions.cancel')}
        busy={revoke.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) revoke.mutate(pending)
        }}
      />
    </ScrollPage>
  )
}

function HowToConnect({ mcpUrl }: { mcpUrl: string }) {
  const { t } = useTranslation('agents')
  const command = `claude mcp add --transport http hivanexus ${mcpUrl}`
  return (
    <section className="rounded-lg border border-line bg-surface p-5">
      <h2 className="font-ui text-sm font-semibold text-ink">{t('connected.how_to_title')}</h2>
      <p className="mt-1 font-body text-sm text-ink-muted">{t('connected.how_to_body')}</p>
      <CopyField value={mcpUrl} />
      <p className="mt-4 font-ui text-xs font-semibold text-ink-subtle">
        {t('connected.claude_code_label')}
      </p>
      <CopyField value={command} />
    </section>
  )
}

function CopyField({ value }: { value: string }) {
  const { t } = useTranslation('agents')
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="mt-2 flex items-center gap-2 rounded-md border border-line bg-surface-muted px-3 py-2">
      <code className="min-w-0 flex-1 truncate font-mono text-xs text-ink">{value}</code>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={copied ? t('connected.copied') : t('connected.copy')}
        className="inline-flex items-center gap-1 rounded px-2 py-1 font-ui text-xs text-ink-muted
                   hover:text-ink focus:outline-none focus:ring-2 focus:ring-brand/40"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-official" aria-hidden="true" />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {copied ? t('connected.copied') : t('connected.copy')}
      </button>
    </div>
  )
}

function Th({ children }: { children?: ReactNode }) {
  return <th className="px-4 py-2 text-left font-semibold">{children}</th>
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 align-top font-body text-sm text-ink-muted ${className}`}>{children}</td>
}

